import {
  ACTION_ARGS,
  Arg,
  COMPONENT_STATE_ARGS,
  Metadata,
  pick,
} from '../../../utils/spds/metadata';
import { hash } from '@ember/helper';
import { testAwareWaitForLater } from '../../../utils/waiters';
import { tracked } from '@glimmer/tracking';
import Body from './feature-banner/body.gjs';
import CTA from './feature-banner/cta-container.gjs';
import CloseButton from '../action/button/close.gjs';
import FlexColumn from '../layout/flex/column.gjs';
import FlexRow from '../layout/flex/row.gjs';
import Header from './feature-banner/header.gjs';
import Super from '../core/base.gjs';
import styles from './feature-banner.module.scss';
import svgJar from 'ember-svg-jar/helpers/svg-jar';

const SUPPORTED_IMAGE_TYPES = ['gif', 'jpeg', 'jpg', 'png', 'webp'];

const METADATA = Metadata.define({
  description: `
A feature banner announces a new or significantly updated feature without disrupting the user workflow.

### Contextual components
The header, body, and CTA container are passed into the feature banner as yielded components using the \`Header\`, \`Body\`, and \`CtaContainer\` keys.

### CTA buttons
The CTA buttons are not implemented as part of the feature banner component and will need to be added for each implementation where appropriate.

### @animateIn argument
The \`@animateIn\` argument should be used for the case when a feature banner will be added to the screen after the rest of the page content.
If the banner will exist as part of the page content on initial render, do not use \`@animateIn\`.

### @illustration argument
The \`@illustration\` argument must be a publically available SVG file.

### @onClose argument
The \`@onClose\` argument controls whether the close button appears within the feature banner.
If a feature banner is not intended to be dismissable, do not pass an \`@onClose\` argument to the banner.
  `,
  extends: [
    pick(ACTION_ARGS, [
      { name: 'onClose', description: 'Action triggered when the banner is dismissed' },
    ]),
    pick(COMPONENT_STATE_ARGS, [
      { name: 'animated', description: 'Enables entrance animation for the banner' },
    ]),
  ],
  meta: {
    altText: Arg.string({ description: 'Alt text for the illustration image' })
      .initial('illustration for banner')
      .required(),
    animateIn: Arg.boolean({
      default: false,
      description: 'Enables entrance animation for the banner',
      initialValue: true,
    }),
    gap: Arg.string({ default: '24px', description: 'Gap between banner columns' }),
    illustration: Arg.string({
      description: 'Image URL or SVG name for the illustration',
    })
      .initial('feature-banner-example-illustration')
      .required(),
    justifyContent: Arg.string({
      default: 'space-between',
      description: 'CSS justify-content for the banner layout',
    }),
    shaded: Arg.boolean({ default: false, description: 'Applies a shaded background style' }),
  },
  name: 'Feature Banner',
  resources: {
    zeroheight: 'https://zeroheight.com/3ddd0f892/p/624698-feature-banner',
  },
});

export default class SpdsFeedbackFeatureBanner extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);
  baseClass = 'spds-feedback-feature-banner';
  classesToMap = ['animateIn', 'open', 'shaded'];
  styles = styles;

  @tracked open = true;

  get animateIn() {
    return this.argOrDefault('animateIn');
  }

  get fileType() {
    if (!this.args.illustration) return null;
    return this.args.illustration.split('.').pop().toLowerCase();
  }

  get gap() {
    return this.argOrDefault('gap');
  }

  get isSupportedFileType() {
    return SUPPORTED_IMAGE_TYPES.includes(this.fileType);
  }

  get justifyContent() {
    return this.argOrDefault('justifyContent');
  }

  get shaded() {
    return this.argOrDefault('shaded');
  }

  close = async () => {
    this.open = false;

    await testAwareWaitForLater(300);

    this.args.onClose?.();
  };

  <template>
    <FlexRow
      @gap={{this.gap}}
      @justifyContent={{this.justifyContent}}
      class={{this.classes}}
      data-test-spds-feedback-feature-banner
      {{this.validateArguments}}
      ...attributes
    >
      <FlexColumn
        @flex="2"
        @gap="24px"
        @justifyContent="center"
        class="spds-feedback-feature-banner-column"
        data-test-spds-feedback-feature-banner-column
      >
        <FlexColumn
          @gap="4px"
          class="spds-feedback-feature-banner-text-container"
          data-test-spds-feedback-feature-banner-text-container
        >
          {{yield
            (hash
              Header=(component Header)
              Body=(component Body)
              CtaContainer=(component CTA)
              close=this.close
            )
          }}
        </FlexColumn>
      </FlexColumn>
      <FlexRow
        @align-items="center"
        @flex="1"
        @justifyContent="flex-end"
        @tag="figure"
        class="spds-feedback-feature-banner-illustration"
        data-test-spds-feedback-feature-banner-illustration
      >
        {{#if this.isSupportedFileType}}
          <img
            alt={{@altText}}
            class="spds-feedback-feature-banner-image"
            data-test-spds-feedback-feature-banner-illustration-image={{this.fileType}}
            src={{@illustration}}
          />
        {{else}}
          {{svgJar
            @illustration
            data-test-spds-feedback-feature-banner-illustration-image="svg"
            desc=@altText
            role="img"
            title=@illustration
          }}
        {{/if}}
      </FlexRow>
      {{#if @onClose}}
        <CloseButton
          @alignSelf="flex-start"
          @close={{this.close}}
          class="spds-feedback-feature-banner-close-btn"
          data-test-spds-feedback-feature-banner-close-button
        />
      {{/if}}
    </FlexRow>
  </template>
}

export const FEATURE_BANNER_METADATA = SpdsFeedbackFeatureBanner.metadata;
